import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { SelectOption, ButtonV3Component, IconV3Component } from '@gea/digital-ui-lib';
import { DashboardService } from '../../services/dashboard.service';
import { LayoutService } from '@gea/digital-ui-lib';
import { DashboardDTO, KPIData, ProductSlides, TopProducts } from './dashboard.model';
import { Subject, takeUntil } from 'rxjs';
import { Carousel } from 'primeng/carousel';
import { DashboardCardComponent } from '../../shared/components/dashboard-card/dashboard-card.component';
import { DashboardProductsCardComponent } from '../../shared/components/dashboard-products-card/dashboard-products-card.component';
import { PrimeTemplate } from 'primeng/api';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonV3Component,
    DashboardCardComponent,
    DashboardProductsCardComponent,
    Carousel,
    PrimeTemplate,
    NgClass,
    IconV3Component,
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  pendingProjects = signal<KPIData>({
    title: 'Pending Projects',
    count: '',
    details: '',
  });
  completedProjects = signal<KPIData>({
    title: 'Completed Projects',
    count: '',
    details: '',
  });
  topProducts: TopProducts = {
    title: 'Top 3 Products',
    products: [],
  };
  isLoading = signal<boolean>(true);
  intervalId!: ReturnType<typeof setInterval>;
  autoplayInterval: number = 5000;
  productSlides: ProductSlides[] = [
    {
      title: 'V XHP Reciprocating Compressor',
      productImgSrc: '../../assets/home/GEA_V-XHP_oben_blau.png',
      text: 'A high-pressure ammonia reciprocating compressor making the most efficient NH₃ heat pump GEA RedGenium best-in-class.',
      altText: 'V HXP',
      classes: 'vxp',
      link: 'https://www.gea.com/en/products/compressors/reciprocating-industrial-compressors/gea-v-xhp/',
    },
    {
      title: 'BluAstrum',
      productImgSrc: '../../assets/home/gea_bluastrum_chiller.png',
      text: 'GEA BluAstrum provides an econonimcal entry into the GEA Blu chiller series, without compromises regarding the technological concept.',
      altText: 'Blu Astrum',
      classes: 'blu-astrum',
      link: 'https://www.gea.com/en/products/chillers/bluastrum-compact-screw-chiller/',
    },
    {
      title: 'Blu-Red Fusion',
      productImgSrc: '../../assets/home/BluRedFusion.PNG',
      text: 'GEA has launched an innovation that combines highly efficient cooling and heating in a single product. The new GEA Blu-Red Fusion is a combination of the successful GEA Blu chillers (low stage) and GEA Red heat pumps (high stage).',
      altText: 'Blu-Red Fusion',
      classes: 'blu-red-fusion',
      link: 'https://www.gea.com/en/products/chillers/gea-blu-red-fusion/',
    },
    {
      title: 'Omni Retrofit Panel',
      productImgSrc: '../../assets/home/gea-omni-control-panel.png',
      text: 'The intuitive touch for screw and piston compressor control. Powerful, yet approachable. Cerebral, yet intuitive. Sophisticated, yet simple. Simply – GEA Omni.',
      altText: 'Omni',
      classes: 'omni',
      link: 'https://www.gea.com/en/products/compressors/omni-retrofit-panel/',
    },
  ];
  destroy$ = new Subject<void>();
  // Set a limit for the displayed pending project names in the KPI
  displayedProjCount = 3;
  @ViewChild('productInfoCarousel') productInfoCarousel!: Carousel;
  @ViewChild('imageCarousel') imageCarousel!: Carousel;

  constructor(
    private readonly dashboardService: DashboardService,
    public layout: LayoutService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.dashboardService
      .getDashboardDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: DashboardDTO) => {
        this.constructKPIDataForCards(data);
        this.isLoading.set(false);
      });
    this.startAutoplay();
  }

  constructKPIDataForCards(data: DashboardDTO): void {
    this.pendingProjects.update((existingValues) => ({
      ...existingValues,
      count: data.pendingProjects.length.toString(),
      details: this.formatProjectsList(data.pendingProjects, this.displayedProjCount),
    }));

    this.completedProjects.update((existingValues) => ({
      ...existingValues,
      details: this.formatProjectsList(data.completedProjects, this.displayedProjCount),
      count: data.completedProjects.length.toString(),
    }));

    this.topProducts = {
      ...this.topProducts,
      products: this.getTopProductsSelectOptions(data.topProducts),
    };
  }

  formatProjectsList(projectsArr: string[], displayedProjCount: number): string {
    const projects = projectsArr.slice(0, displayedProjCount).join(', ');
    return projectsArr.length > displayedProjCount ? projects + ' ...' : projects;
  }

  getTopProductsSelectOptions(topProducts: string[]): SelectOption[] {
    const topProductSelectOptions: SelectOption[] = [];
    topProducts.forEach((product: string, index: number) => {
      topProductSelectOptions.push({
        value: index + 1,
        name: product,
      });
    });
    return topProductSelectOptions;
  }

  nextFeed(): void {
    if (this.productInfoCarousel && this.imageCarousel) {
      this.productInfoCarousel.navForward(new MouseEvent('click'));
      this.imageCarousel.navForward(new MouseEvent('click'));
    }
    this.restartAutoplay();
  }

  prevFeed(): void {
    if (this.productInfoCarousel && this.imageCarousel) {
      this.productInfoCarousel.navBackward(new MouseEvent('click'));
      this.imageCarousel.navBackward(new MouseEvent('click'));
    }
    this.restartAutoplay();
  }

  startAutoplay(): void {
    this.intervalId = setInterval(() => {
      if (this.productSlides && this.productSlides.length > 1) {
        this.productInfoCarousel.navForward(new MouseEvent('click'));
        this.imageCarousel.navForward(new MouseEvent('click'));
      }
    }, this.autoplayInterval);
  }

  stopAutoplay(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  restartAutoplay(): void {
    this.stopAutoplay();
    this.startAutoplay();
  }

  goToProjectsLastUpdated(): void {
    this.router.navigate(['/projects'], {
      queryParams: {
        sort: 'lastUpdated',
        order: 'desc',
      },
    });
  }

  onSliderClick(id: number): void {
    if (this.productInfoCarousel && this.imageCarousel) {
      // Force both carousels to the specific page
      this.productInfoCarousel.page = id;
      this.imageCarousel.page = id;

      // Trigger change detection to ensure both carousels update
      this.productInfoCarousel.cd.detectChanges();
      this.imageCarousel.cd.detectChanges();
    }
    this.restartAutoplay();
  }

  redirectToProduct(redirectUrl: string): void {
    this.openInNewTab(redirectUrl);
  }

  openInNewTab(url: string): void {
    window.open(url, '_blank')?.focus();
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
